// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState, useMemo, useCallback } from 'react';
import { LineChart } from '@carbon/charts-react';
import { ScaleTypes } from '@carbon/charts';
import type { LineChartOptions } from '@carbon/charts';
import { Button } from '@carbon/react';
import { ChevronRight, Download } from '@carbon/icons-react';
import type { RawInputs, RawDevice } from '../../../types';
import { useShellStore } from '../../../stores/shellStore';

/** Human-readable label from a leaf-path array, e.g. ['evaporator','inlet','T'] → "Evaporator · Inlet · T". */
function formatPath(path: string[]): string {
  return path.map(seg => seg.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(' · ');
}

/**
 * Units are no longer carried in the payload, so infer them from the leaf
 * quantity name. Values were validated against the sample narration (W ≈ kW,
 * T in °C, Q thermal in kW, volumetric flow in m³/s, pump ΔP in kPa).
 */
function inferUnit(leafKey: string): string {
  switch (leafKey) {
    case 'W':
    case 'Q':
      return 'kW';
    case 'T':
      return '°C';
    case 'V_dot':
      return 'm³/s';
    case 'DP':
      return 'kPa';
    case 'onoff_status':
      return 'on/off';
    default:
      return '';
  }
}

interface DeviceSeries {
  label: string;
  unit: string;
  values: number[];
}

/**
 * Walk a device's block tree and collect every leaf that is a per-timestep
 * numeric array into a flat list of chartable series. Non-numeric leaves and
 * empty/null blocks (e.g. an AHU with no outdoor-air stream) are skipped.
 */
function flattenDevice(device: RawDevice): DeviceSeries[] {
  const series: DeviceSeries[] = [];

  const walk = (node: unknown, path: string[]): void => {
    if (Array.isArray(node)) {
      if (node.length > 0 && typeof node[0] === 'number') {
        series.push({
          label: formatPath(path),
          unit: inferUnit(path[path.length - 1]),
          values: node as number[],
        });
      }
      return;
    }
    if (node && typeof node === 'object') {
      for (const [key, value] of Object.entries(node)) {
        walk(value, [...path, key]);
      }
    }
  };

  for (const [block, value] of Object.entries(device)) {
    if (block === 'id' || block === 'type') continue;
    walk(value, [block]);
  }

  return series;
}

interface DeviceChartProps {
  device: RawDevice;
  timeLabels: string[];
  theme: string;
}

function DeviceChart({ device, timeLabels, theme }: DeviceChartProps) {
  const [open, setOpen] = useState(false);

  const series = useMemo(() => flattenDevice(device), [device]);

  const uniqueUnits = useMemo(() => {
    const seen: string[] = [];
    for (const s of series) {
      if (s.unit && !seen.includes(s.unit)) seen.push(s.unit);
    }
    return seen;
  }, [series]);

  // Carbon charts tabular data: one row per (series, timestep).
  const chartData = useMemo(() => {
    if (!open) return [];
    const data: { group: string; key: string; value: number }[] = [];
    for (const s of series) {
      const group = `${s.label}${s.unit ? ` (${s.unit})` : ''}`;
      s.values.forEach((value, i) => {
        data.push({ group, key: timeLabels[i] ?? String(i), value });
      });
    }
    return data;
  }, [open, series, timeLabels]);

  const chartOptions = useMemo<LineChartOptions>(
    () => ({
      axes: {
        bottom: { mapsTo: 'key', scaleType: ScaleTypes.LABELS },
        left: {
          mapsTo: 'value',
          title: uniqueUnits.join(' / ') || undefined,
          scaleType: ScaleTypes.LINEAR,
        },
      },
      curve: 'curveMonotoneX',
      points: { enabled: false },
      legend: { enabled: true },
      toolbar: { enabled: false },
      height: '320px',
      theme,
    }),
    [uniqueUnits, theme]
  );

  return (
    <div className="sched-chart-device">
      <button
        className="sched-chart-device-header"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(prev => !prev)}
      >
        <ChevronRight
          size={16}
          className={`sched-chart-chevron ${open ? 'sched-chart-chevron-open' : ''}`}
        />
        <span className="sched-chart-device-name">{device.id}</span>
        <span className="sched-chart-device-type">
          {device.type} · {series.length} signal{series.length !== 1 ? 's' : ''}
        </span>
      </button>

      {open && chartData.length > 0 && (
        <div className="sched-chart-device-body">
          <LineChart data={chartData} options={chartOptions} />
        </div>
      )}
    </div>
  );
}

function downloadCsv(rawInputs: RawInputs, timeLabels: string[]): void {
  const columns: { header: string; values: number[] }[] = [];
  for (const device of rawInputs.optimization_result.devices) {
    for (const s of flattenDevice(device)) {
      const header = `${device.id} - ${s.label}${s.unit ? ` (${s.unit})` : ''}`;
      columns.push({ header, values: s.values });
    }
  }

  const headers = ['Timestamp', ...columns.map(c => c.header)];
  const rows = timeLabels.map((label, i) => [label, ...columns.map(c => c.values[i] ?? '')]);

  const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `equipment_setpoints_${rawInputs.site_id}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

interface PlanChartsProps {
  rawInputs: RawInputs;
}

function PlanCharts({ rawInputs }: PlanChartsProps) {
  const [open, setOpen] = useState(false);
  const theme = useShellStore(s => s.theme);

  const devices = rawInputs.optimization_result?.devices ?? [];

  const timeLabels = useMemo(() => {
    const { start_time, step_seconds, count } = rawInputs.time_axis;
    // Treat an offset-less timestamp as UTC so it stays consistent with the
    // UTC-based label formatting below.
    const hasTz = /(?:Z|[+-]\d{2}:\d{2})$/.test(start_time);
    const start = new Date(hasTz ? start_time : `${start_time}Z`);
    return Array.from({ length: count }, (_, i) => {
      const t = new Date(start.getTime() + i * step_seconds * 1000);
      return t.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC',
      });
    });
  }, [rawInputs.time_axis]);

  const handleDownload = useCallback(() => {
    downloadCsv(rawInputs, timeLabels);
  }, [rawInputs, timeLabels]);

  if (devices.length === 0) return null;

  return (
    <div className="sched-charts">
      <div className="sched-charts-head">
        <button
          className="sched-charts-toggle"
          type="button"
          aria-expanded={open}
          onClick={() => setOpen(prev => !prev)}
        >
          <ChevronRight
            size={16}
            className={`sched-chart-chevron ${open ? 'sched-chart-chevron-open' : ''}`}
          />
          <h3 className="sched-section-title">Equipment Charts ({devices.length})</h3>
        </button>
        {open && (
          <Button kind="ghost" size="sm" renderIcon={Download} onClick={handleDownload}>
            Download CSV
          </Button>
        )}
      </div>

      {open && (
        <div className="sched-charts-body">
          {devices.map(device => (
            <DeviceChart key={device.id} device={device} timeLabels={timeLabels} theme={theme} />
          ))}
        </div>
      )}
    </div>
  );
}

export default PlanCharts;
